import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { Body, Kit, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const RIBS = [0.24, 0.56, 0.84];
const PETALS = 8;
const RISERS = 6;

function ribs(k: Kit, body: Body, color: number, alpha: number): void {
  const { g } = k;
  const r = body.radius;
  for (const w of RIBS) g.ellipse(body.x, body.y, r * w * body.scaleX, r * 0.97 * body.scaleY);
  g.stroke({ width: Math.max(1, r * 0.045), color, alpha });
}

function caps(k: Kit, body: Body, color: number, alpha: number): void {
  const { g } = k;
  const r = body.radius;
  for (const side of [-1, 1]) g.ellipse(body.x, body.y + side * r * 0.9 * body.scaleY, r * 0.3 * body.scaleX, r * 0.075 * body.scaleY);
  g.fill({ color, alpha });
}

function flame(g: Graphics, x: number, y: number, s: number, lean: number): void {
  g.moveTo(x + lean * s * 0.9, y - s * 1.25)
    .bezierCurveTo(x + s * 0.65, y - s * 0.35, x + s * 0.6, y + s * 0.55, x, y + s * 0.6)
    .bezierCurveTo(x - s * 0.6, y + s * 0.55, x - s * 0.65, y - s * 0.35, x + lean * s * 0.9, y - s * 1.25)
    .closePath();
}

function petal(g: Graphics, x: number, y: number, len: number, wide: number, a: number): void {
  const ux = Math.cos(a);
  const uy = Math.sin(a);
  const tx = x + ux * len;
  const ty = y + uy * len;
  const mx = x + ux * len * 0.45;
  const my = y + uy * len * 0.45;
  g.moveTo(x, y)
    .quadraticCurveTo(mx - uy * wide, my + ux * wide, tx, ty)
    .quadraticCurveTo(mx + uy * wide, my - ux * wide, x, y)
    .closePath();
}

function lantern(g: Graphics, x: number, y: number, s: number): void {
  g.roundRect(x - s * 0.6, y - s * 0.8, s * 1.2, s * 1.6, s * 0.55);
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    g.circle(body.x, body.y + r * 0.12 * body.scaleY, r * 0.46).fill({ color: pal.held, alpha: 0.16 * alpha });
    g.circle(body.x, body.y + r * 0.12 * body.scaleY, r * 0.24).fill({ color: pal.heldLight, alpha: 0.18 * alpha });
    ribs(k, body, lerpColor(color, pal.ink, 0.55), 0.16 * alpha);
    caps(k, body, lerpColor(color, pal.ink, 0.45), 0.22 * alpha);
    k.rimLight(body, 0.5 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: fill, alpha });
    g.circle(body.x, body.y + r * 0.1 * body.scaleY, r * 0.62).fill({ color: pal.heldLight, alpha: 0.28 * alpha });
    g.circle(body.x, body.y + r * 0.1 * body.scaleY, r * 0.38).fill({ color: pal.heldLight, alpha: 0.4 * alpha });
    ribs(k, body, lerpColor(fill, pal.heldShade, 0.7), 0.3 * alpha);
    caps(k, body, lerpColor(fill, pal.heldShade, 0.8), 0.35 * alpha);
    const lean = k.reduced ? 0 : 0.18 * Math.sin(k.time * 2.1) + 0.1 * Math.sin(k.time * 5.3);
    flame(g, body.x, body.y + r * 0.08 * body.scaleY, r * 0.2, lean);
    g.fill({ color: pal.heldLight, alpha: 0.9 * alpha });
    flame(g, body.x, body.y + r * 0.16 * body.scaleY, r * 0.1, lean * 0.6);
    g.fill({ color: lerpColor(pal.held, pal.spot, 0.35), alpha: 0.7 * alpha });
    k.terminator(body, lerpColor(fill, pal.heldShade, 0.8), 0.3 * alpha);
    k.rimLight(body, 0.6 * alpha);
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, pal.ink, 0.6), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: 1.1, color: pal.spot, alpha: 0.35 * alpha });
    const len = Math.max(5, k.unit * 0.032);
    k.ringDots(body, reach - len * 0.5, PETALS, spin, (x, y, _i, a) => {
      petal(g, x, y, len, len * 0.42, a);
    });
    g.fill({ color: pal.spot, alpha: 0.72 * alpha });
    k.ringDots(body, reach - len * 0.5, PETALS, spin + Math.PI / PETALS, (x, y, _i, a) => {
      g.circle(x + Math.cos(a) * len * 0.4, y + Math.sin(a) * len * 0.4, Math.max(1.2, len * 0.12));
    });
    g.fill({ color: pal.heldLight, alpha: 0.6 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    g.circle(x, y, radius * 1.15);
    g.fill({ color: on ? (last ? pal.spot : pal.held) : lerpColor(pal.bg, pal.rim, 0.18), alpha: (on ? 0.9 : 0.6) * alpha });
    g.circle(x, y, radius * 1.15).stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
    if (!on) return;
    flame(g, x, y + radius * 0.12, radius * 0.5, 0);
    g.fill({ color: pal.heldLight, alpha: 0.95 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    g.moveTo(points[0]!, points[1]!);
    for (let i = 2; i < count; i += 2) g.lineTo(points[i]!, points[i + 1]!);
    g.stroke({ width: 1.2, color: pal.path, alpha: alpha * 2.2, cap: 'round', join: 'round' });
    const step = Math.max(14, k.unit * 0.09);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const len = Math.hypot(points[i]! - x0, points[i + 1]! - y0);
      const n = Math.min(40, Math.floor(len / step));
      for (let s = 1; s < n; s++) {
        const t = s / n;
        g.circle(x0 + (points[i]! - x0) * t, y0 + (points[i + 1]! - y0) * t, 1.8);
      }
    }
    g.fill({ color: pal.held, alpha: alpha * 2.4 });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 2 === 0) {
      petal(g, x - Math.cos(rot) * size, y - Math.sin(rot) * size, size * 2.2, size * 0.8, rot);
    } else {
      g.circle(x, y, size * 0.55);
    }
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const rise = m.grow * m.r * 0.35;
    const s = Math.max(3, m.unit * 0.022);
    for (let i = 0; i < RISERS; i++) {
      const a = (i / RISERS) * TAU - Math.PI / 2 + 0.3;
      const x = m.x + Math.cos(a) * m.r * 0.92;
      const y = m.y + Math.sin(a) * m.r * 0.92 - rise * (0.7 + 0.3 * ((i * 7) % 3));
      lantern(g, x, y, s);
      g.fill({ color: pal.held, alpha: m.fade * 0.6 });
      lantern(g, x, y, s);
      g.stroke({ width: Math.max(1, m.width * 0.4), color: pal.heldShade, alpha: m.fade * 0.5 });
      g.circle(x, y + s * 0.15, s * 0.32).fill({ color: pal.heldLight, alpha: m.fade * 0.7 });
    }
    const rx = m.r * (0.9 + 0.5 * m.grow);
    g.ellipse(m.x, m.y + m.r * 0.25, rx, rx * 0.32).stroke({ width: m.width * 0.6, color: pal.rim, alpha: m.fade * 0.32 });
    g.ellipse(m.x, m.y + m.r * 0.25, rx * 0.72, rx * 0.22).stroke({ width: m.width * 0.4, color: pal.spot, alpha: m.fade * 0.3 });
  },

  trail(g, x, y, size, angle) {
    g.circle(x, y, size * 0.7);
    g.circle(x + Math.cos(angle) * size * 1.4, y - size * 1.6, size * 0.35);
  },
};
